import { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  useDashboardFederationsControllerAvailableByPublicId,
  useDashboardFederationsControllerAvailableCount,
  useDashboardFederationsControllerMeAvailable,
  useDashboardFederationsControllerReadableCount,
} from "@/api/__generated__/dashboard-federations/dashboard-federations";
import { useAuthContext } from "@/contexts/AuthContext";
import { useCurrentPlatformContext } from "@/contexts/CurrentPlatformContext";
import { useCurrentUserContext } from "@/contexts/CurrentUserContext";
import { useLocalStorage } from "@/hooks/common/useLocalStorage";
// Deep import avoids a circular dependency: `@/hooks` barrel transitively re-exports
// `useScopedHref`, which depends on this provider.
import { useScopedRouteParams } from "@/hooks/common/useScopedRouteParams";
import { mapToAvailableFederation } from "@/mappers";
import { ForbiddenPage } from "@/ui";
import { buildScopedPath } from "@/utils/common";
import { isFederationForbiddenError, isOrganizationAttachedToPlatform } from "@/utils/federation";

import { clearCurrentLeagueId } from "../CurrentLeagueContext/CurrentLeagueContext.utils";

import { CurrentOrganizationContext, SwitchOrganizationGuard } from "./CurrentOrganizationContext";
import {
  isRestPathHiddenInPlatformOnlyMode,
  PLATFORM_ONLY_FALLBACK_REST_PATH,
} from "./CurrentOrganizationContext.utils";

export const CurrentOrganizationProvider = ({ children }: PropsWithChildren) => {
  const { currentUser } = useCurrentUserContext();
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const scopedParams = useScopedRouteParams();
  const urlOrganizationId = scopedParams.organizationId;

  const [storedOrganizationId, setCurrentOrganizationId] = useLocalStorage<string | null>(
    "organizationId",
    null,
    { subscribe: "currentDocument" }
  );

  // Dedicated flag so a deliberate platform-only choice survives reloads / unscoped
  // cold loads (before the enforcer has canonicalized the URL).
  const [storedIsPlatformOnly, setStoredIsPlatformOnly] = useLocalStorage<boolean>(
    "scopeIsPlatformOnly",
    false,
    { subscribe: "currentDocument" }
  );

  const {
    platform: currentPlatform,
    loading: currentPlatformLoading,
    consumePlatformSwitchIntent,
  } = useCurrentPlatformContext();

  // Null-safe equivalent of `useCanSelectWholePlatform()` — this provider
  // renders during the splash phase, so read the capability set straight off
  // the (possibly unresolved) platform record instead of the hook. Any
  // capability on the platform's PLATFORM node — including read-only,
  // connection-conferred keys — admits the whole-platform scope; an empty set
  // fails closed.
  const canSelectWholePlatform = !!currentPlatform && currentPlatform.capabilities.size > 0;

  // Platform-only mode = org intentionally absent. The URL is the source of truth
  // on a scoped URL; otherwise fall back to the persisted flag. Either way it only
  // holds when the user holds platform-level access — a capability-less user can
  // never be platform-only.
  const isPlatformOnly = scopedParams.isScoped
    ? canSelectWholePlatform && urlOrganizationId === null
    : canSelectWholePlatform && storedIsPlatformOnly;

  const currentOrganizationId: string | null = isPlatformOnly
    ? null
    : (urlOrganizationId ?? storedOrganizationId);

  // No `capabilities` param → the legacy membership count (drives cold-start /
  // whole-platform affordances). Per-page filter visibility uses the
  // capability-scoped `useFederationFilterCount` instead. See docs/filters.md.
  const { data: readableFederationsCountData } = useDashboardFederationsControllerReadableCount(
    undefined,
    { query: { enabled: !!currentUser } }
  );
  const accessibleFederationsCount = readableFederationsCountData?.total ?? 0;

  const { data: availableFederationsCountData } = useDashboardFederationsControllerAvailableCount({
    query: { enabled: isAuthenticated },
  });
  const availableFederationsCount = availableFederationsCountData?.total ?? 0;

  // Stored / URL id → full record via REST `GET /federations/available/:id`.
  // 404 means the id is outside the caller's available set or doesn't exist —
  // both surface the same status. FE treats either case as "stale id, clear it".
  // The current platform's id pair-scopes the returned `capabilities` set to
  // the `(platform, federation)` pair (grants held at other platforms don't
  // surface) and is part of the query key, so the set refetches on platform
  // switch. Wait for the platform context to resolve so the query fires with a
  // real id; if the platform genuinely fails to resolve, fetch without it and
  // accept the platform-agnostic ORG-node set.
  const {
    data: byIdData,
    isLoading: byIdLoading,
    error: byIdError,
  } = useDashboardFederationsControllerAvailableByPublicId(
    currentOrganizationId ?? "",
    currentPlatform ? { platformId: currentPlatform.id } : undefined,
    {
      query: {
        enabled: isAuthenticated && currentOrganizationId !== null && !currentPlatformLoading,
      },
    }
  );

  const isStaleOrganizationId = isFederationForbiddenError(byIdError);
  const isFatalByIdError = !!byIdError && !isStaleOrganizationId;

  // Resolve a default org only when NOT platform-only. In platform-only mode the org
  // is intentionally null and must not auto-resolve.
  const needsFirstFallback =
    isAuthenticated && !isPlatformOnly && (currentOrganizationId === null || isStaleOrganizationId);

  // Cold start (no stored id) or stale stored id → REST `me/available`.
  // 200 returns the caller's first available federation; 204 → caller has none.
  // Scope the pick to the current platform so a user with federations across
  // several platforms bootstraps into one on the platform they're actually in;
  // skip until the platform context resolves so the query fires with a real id.
  const {
    data: firstDefaultData,
    isLoading: firstDefaultLoading,
    error: firstDefaultError,
  } = useDashboardFederationsControllerMeAvailable(
    { platformId: currentPlatform?.id },
    {
      query: {
        enabled: needsFirstFallback && !!currentPlatform && !currentPlatformLoading,
      },
    }
  );

  const firstFallbackId = firstDefaultData?.id ?? null;

  // Both byId and me/available return `DashboardFederationAvailable` — map straight
  // to the `AvailableFederation` domain type. Platform-only mode resolves to null.
  const organization = useMemo(() => {
    if (isPlatformOnly) {
      return null;
    }

    if (byIdData && !isStaleOrganizationId) {
      return mapToAvailableFederation(byIdData);
    }

    if (firstDefaultData) {
      return mapToAvailableFederation(firstDefaultData);
    }

    return null;
  }, [isPlatformOnly, byIdData, isStaleOrganizationId, firstDefaultData]);

  // R3 pair-attachment guard: a scoped URL pairing an org with a platform it is
  // not attached to (`/platform/B/entity/F/…` where F doesn't operate on B) must
  // not render an editable org context — the fees save would 403 server-side but
  // the sports save would not. Treated exactly like the stale/unavailable-id
  // case below. Empty attachment list = unknown (deploy gap) → fails open.
  const isUnattachedPair =
    !isPlatformOnly &&
    !!organization &&
    !!currentPlatform &&
    !isOrganizationAttachedToPlatform(organization.attachedPlatformIds, currentPlatform.id);

  const switchOrganizationGuardRef = useRef<SwitchOrganizationGuard | null>(null);

  const setSwitchOrganizationGuard = useCallback((guard: SwitchOrganizationGuard | null) => {
    switchOrganizationGuardRef.current = guard;
  }, []);

  // Latest `canSelectWholePlatform` for the switch callback without re-creating it
  // on every render — `switchOrganization(null)` must read the current permission.
  const canSelectWholePlatformRef = useRef(canSelectWholePlatform);
  canSelectWholePlatformRef.current = canSelectWholePlatform;

  const switchOrganization = useCallback(
    (organizationId: string | null) => {
      // Entering platform-only requires at least one platform-level capability;
      // coerce to a no-op otherwise so a capability-less user can never drop the org.
      if (organizationId === null && !canSelectWholePlatformRef.current) {
        return;
      }

      const proceed = () => {
        // Switching org clears any league — a league belongs to one org. Land on
        // the leagues list rather than carrying over the (now meaningless) league
        // sub-path that the current URL holds, and drop the persisted league id so
        // it can't re-inject on a later cold/unscoped load.
        clearCurrentLeagueId();
        const restPath = scopedParams.leagueId !== null ? "tours" : scopedParams.restPath;

        if (organizationId === null) {
          setStoredIsPlatformOnly(true);
          setCurrentOrganizationId(null);
          if (scopedParams.isScoped && scopedParams.platformId !== null) {
            // Platform-only mode has no current organization, so the pages that
            // render it (entity settings, management team) stop existing — land
            // on the Entities list instead of carrying over a dead sub-path.
            const platformOnlyRestPath = isRestPathHiddenInPlatformOnlyMode(restPath)
              ? PLATFORM_ONLY_FALLBACK_REST_PATH
              : restPath;
            navigate(buildScopedPath(scopedParams.platformId, null, null, platformOnlyRestPath), {
              replace: false,
            });
          }
          return;
        }

        setStoredIsPlatformOnly(false);
        setCurrentOrganizationId(organizationId);
        if (scopedParams.isScoped && scopedParams.platformId !== null) {
          navigate(buildScopedPath(scopedParams.platformId, organizationId, null, restPath), {
            replace: false,
          });
        }
      };
      const guard = switchOrganizationGuardRef.current;
      if (guard && guard(proceed)) return;
      proceed();
    },
    [
      setCurrentOrganizationId,
      setStoredIsPlatformOnly,
      scopedParams.isScoped,
      scopedParams.platformId,
      scopedParams.leagueId,
      scopedParams.restPath,
      navigate,
    ]
  );

  // Guard against re-clear loops if the fallback also Forbiddens.
  const lastClearedStaleIdRef = useRef<string | null>(null);

  const previousPlatformIdRef = useRef<string | null>(currentPlatform?.id ?? null);
  useEffect(() => {
    const nextPlatformId = currentPlatform?.id ?? null;
    if (nextPlatformId === null) return;
    if (previousPlatformIdRef.current === nextPlatformId) return;

    const previousPlatformId = previousPlatformIdRef.current;
    previousPlatformIdRef.current = nextPlatformId;

    if (previousPlatformId === null) return;

    // Only an intentional `switchPlatform` (sidebar-style switch) resets the org.
    // Direct URL navigations that change the platform (deep links, scoped links
    // that already carry an explicit entity) keep the URL-supplied organization.
    if (!consumePlatformSwitchIntent()) return;

    // A deliberate platform-only mode survives a platform switch for managers —
    // don't force the new platform's data-owner org onto a platform-only user.
    if (isPlatformOnly) return;

    // On platform switch, land on the platform's data-owner federation — the
    // federation that holds rights over the platform — falling back to the legacy
    // `owner` only when `dataOwner` is unset (legacy rows that predate backfill).
    const nextOrganizationId = currentPlatform?.dataOwner?.id ?? currentPlatform?.owner?.id ?? null;
    if (nextOrganizationId !== null && nextOrganizationId !== currentOrganizationId) {
      setCurrentOrganizationId(nextOrganizationId);
      if (scopedParams.isScoped && scopedParams.platformId !== null) {
        navigate(
          buildScopedPath(scopedParams.platformId, nextOrganizationId, null, scopedParams.restPath),
          {
            replace: true,
          }
        );
      }
    }
  }, [
    currentPlatform?.id,
    currentPlatform?.dataOwner?.id,
    currentPlatform?.owner?.id,
    currentOrganizationId,
    isPlatformOnly,
    scopedParams.isScoped,
    scopedParams.platformId,
    scopedParams.restPath,
    navigate,
    setCurrentOrganizationId,
    consumePlatformSwitchIntent,
  ]);

  // A scoped URL with no `/entity/:o` segment is only valid in platform-only
  // mode (a platform manager who deliberately cleared the org). Anyone else who
  // lands on such a URL — a direct load / deep link, or a switch to a platform
  // they can only access as a member — must be given an organization: preselect
  // the platform's data-owner federation and canonicalize the URL to include it.
  // The intentional-switch path is already handled by the effect above; this is
  // the no-switch-intent safety net.
  useEffect(() => {
    if (!scopedParams.isScoped || scopedParams.platformId === null) return;
    if (urlOrganizationId !== null) return; // URL already carries an org
    if (isPlatformOnly) return; // legitimate platform-only — keep it
    if (!currentPlatform) return; // wait for the platform to resolve

    const targetOrganizationId =
      currentPlatform.dataOwner?.id ?? currentPlatform.owner?.id ?? firstFallbackId;
    if (targetOrganizationId === null) return; // nothing selectable — leave as-is

    setCurrentOrganizationId(targetOrganizationId);
    navigate(
      buildScopedPath(scopedParams.platformId, targetOrganizationId, null, scopedParams.restPath),
      { replace: true }
    );
  }, [
    scopedParams.isScoped,
    scopedParams.platformId,
    scopedParams.restPath,
    urlOrganizationId,
    isPlatformOnly,
    currentPlatform,
    firstFallbackId,
    navigate,
    setCurrentOrganizationId,
  ]);

  useEffect(() => {
    // Platform-only mode: the org is intentionally null — nothing to resolve / clear.
    if (isPlatformOnly) {
      return;
    }

    if (isStaleOrganizationId && scopedParams.isScoped) {
      // URL-driven forbidden — render ForbiddenPage below, don't silently swap.
      return;
    }

    if (isStaleOrganizationId && lastClearedStaleIdRef.current !== currentOrganizationId) {
      lastClearedStaleIdRef.current = currentOrganizationId;
      setCurrentOrganizationId(firstFallbackId);
      return;
    }

    if (currentOrganizationId === null && organization) {
      setCurrentOrganizationId(
        currentPlatform?.dataOwner?.id ?? currentPlatform?.owner?.id ?? organization.id
      );
    }
  }, [
    isPlatformOnly,
    isStaleOrganizationId,
    currentOrganizationId,
    organization,
    currentPlatform?.dataOwner?.id,
    currentPlatform?.owner?.id,
    firstFallbackId,
    scopedParams.isScoped,
    setCurrentOrganizationId,
  ]);

  const loading =
    (currentOrganizationId !== null && (currentPlatformLoading || (byIdLoading && !byIdData))) ||
    (needsFirstFallback && firstDefaultLoading);
  const isError = isFatalByIdError || !!firstDefaultError;

  const contextValue = useMemo(
    () => ({
      organization,
      isPlatformOnly,
      accessibleFederationsCount,
      availableFederationsCount,
      loading,
      switchOrganization,
      setSwitchOrganizationGuard,
      isError,
    }),
    [
      organization,
      isPlatformOnly,
      accessibleFederationsCount,
      availableFederationsCount,
      loading,
      switchOrganization,
      setSwitchOrganizationGuard,
      isError,
    ]
  );

  if (scopedParams.isScoped && (isStaleOrganizationId || isUnattachedPair)) {
    return <ForbiddenPage />;
  }

  return (
    <CurrentOrganizationContext.Provider value={contextValue}>
      {children}
    </CurrentOrganizationContext.Provider>
  );
};
